/**
 * NextActionsChecklist - Phase 3.2 Accountant Dashboard Enhancement
 * 
 * Shows a dynamic checklist of next actions based on claim state.
 * Tracks completion and shows impact of each action.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  ClipboardList,
  CheckCircle,
  Circle,
  ArrowRight,
  AlertTriangle,
  Loader2,
  Sparkles
} from 'lucide-react';

interface NextAction {
  id: string;
  action: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  impact: string;
  completed: boolean;
  due_date?: string;
  route?: string;
}

interface NextActionsChecklistProps {
  reportId: string;
  className?: string;
  onActionComplete?: (actionId: string) => void;
}

const PRIORITY_STYLES = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
    icon: 'text-red-600'
  },
  high: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    icon: 'text-orange-600'
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    badge: 'bg-yellow-100 text-yellow-700',
    icon: 'text-yellow-600'
  },
  low: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    badge: 'bg-gray-100 text-gray-700',
    icon: 'text-gray-600'
  }
};

export default function NextActionsChecklist({ 
  reportId, 
  className = '',
  onActionComplete 
}: NextActionsChecklistProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [actions, setActions] = useState<NextAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);

  useEffect(() => {
    if (reportId) fetchActions();
  }, [reportId]);

  const fetchActions = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/dashboard/next-actions/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setActions(data.actions || []);
    } catch (err) {
      console.debug('Next actions API not available');
      setActions([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleAction = async (action: NextAction) => {
    if (action.completed) return; // Can't uncomplete

    setCompletingId(action.id);
    try {
      const token = await getToken();
      await api.post(`/api/v1/dashboard/next-actions/${action.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });

      setActions(prev => prev.map(a =>
        a.id === action.id ? { ...a, completed: true } : a
      ));

      onActionComplete?.(action.id);
    } catch (err) {
      console.debug('Failed to mark action complete');
    } finally {
      setCompletingId(null);
    }
  };

  const completedCount = actions.filter(a => a.completed).length;
  const progress = actions.length > 0 ? (completedCount / actions.length) * 100 : 0;

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <ClipboardList className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Next Actions</h3>
          </div>
          <span className="text-sm text-gray-500">
            {completedCount}/{actions.length} complete
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-600 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Actions list */}
      <div className="divide-y divide-gray-100">
        {error && actions.length === 0 ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">Next actions unavailable</p>
            <button
              onClick={fetchActions}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        ) : actions.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
            <p className="text-gray-600 font-medium">All actions complete!</p>
            <p className="text-gray-500 text-sm">This claim is ready for the next step</p>
          </div>
        ) : (
          actions.map((action) => {
            const styles = PRIORITY_STYLES[action.priority];
            const isCompleting = completingId === action.id;

            return (
              <div 
                key={action.id}
                className={`p-4 ${action.completed ? 'bg-gray-50 opacity-75' : styles.bg} transition-all`}
              >
                <div className="flex items-start">
                  <button
                    onClick={() => toggleAction(action)}
                    disabled={action.completed || isCompleting}
                    className="mt-0.5 mr-3 flex-shrink-0"
                  >
                    {isCompleting ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    ) : action.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Circle className={`w-5 h-5 ${styles.icon} hover:text-blue-600 transition-colors`} />
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h4 className={`font-medium ${action.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {action.action}
                      </h4>
                      {!action.completed && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}>
                          {action.priority}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-1 ${action.completed ? 'text-gray-400' : 'text-gray-600'}`}>
                      {action.description}
                    </p>
                    {!action.completed && (
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Impact: {action.impact}
                      </div>
                    )}
                  </div>

                  {action.route && !action.completed && (
                    <button
                      onClick={() => navigate(action.route!)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Go to action"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
